import React, { useState } from 'react';
import { Row, Col, Card, Statistic, Table, Select, Typography, Button, Space, Input, Tag, Tooltip, Empty } from 'antd';
import { ReloadOutlined, UserOutlined, TeamOutlined, RiseOutlined, BarChartOutlined, SearchOutlined, ShopOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;

interface SearchShop {
  bot_id: number;
  bot_name: string;
  bot_username: string;
  visit_days: number;
  last_visit?: string | null;
}

interface SearchUser {
  telegram_id: number;
  username: string;
  visit_days: number;
  shop_count: number;
  last_visit?: string | null;
  shops: SearchShop[];
}

interface SearchBot {
  bot_id: number;
  bot_name: string;
  bot_username: string;
  is_active: boolean;
  visitor_count: number;
  visit_days: number;
  record_count: number;
  last_visit?: string | null;
}

interface SearchResult {
  keyword: string;
  days: number;
  start_date: string;
  end_date: string;
  summary: {
    matched_users: number;
    matched_bots: number;
    total_visitors: number;
    total_records: number;
  };
  users: SearchUser[];
  bots: SearchBot[];
  note: string;
}

const TrafficMonitor: React.FC = () => {
  const [trendDays, setTrendDays] = useState(30);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchDays, setSearchDays] = useState(30);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [searched, setSearched] = useState(false);

  const { data: overview, isLoading: overviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['traffic-overview'],
    queryFn: analyticsApi.getTrafficOverview,
  });

  const { data: trend, refetch: refetchTrend } = useQuery({
    queryKey: ['traffic-trend', trendDays],
    queryFn: () => analyticsApi.getTrafficTrend(trendDays),
  });

  const { data: bots, isLoading: botsLoading, refetch: refetchBots } = useQuery({
    queryKey: ['traffic-bots'],
    queryFn: analyticsApi.getTrafficBots,
  });

  const handleRefresh = () => {
    refetchOverview();
    refetchTrend();
    refetchBots();
  };

  const handleSearch = async (kw?: string) => {
    const keyword = (kw ?? searchKeyword).trim();
    if (!keyword) {
      setSearchResult(null);
      setSearched(false);
      return;
    }
    setSearchKeyword(keyword);
    setSearchLoading(true);
    try {
      const res = await analyticsApi.searchTraffic({ keyword, days: searchDays, limit: 30 });
      setSearchResult((res as unknown as SearchResult) || null);
      setSearched(true);
    } catch {
      setSearchResult(null);
      setSearched(true);
    } finally {
      setSearchLoading(false);
    }
  };

  const stats = (overview as any) || {};
  const trendData = (trend as any) || [];
  const botList = (bots as any) || [];

  const chartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const p = params[0];
        return `${p.axisValue}<br/>${p.marker}访客数：${p.value}`;
      },
    },
    xAxis: {
      type: 'category',
      data: trendData.map((item: any) => item.date.substring(5)),
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      name: '访客数',
      minInterval: 1,
    },
    series: [
      {
        name: '访客数',
        type: 'line',
        smooth: true,
        areaStyle: { opacity: 0.15 },
        data: trendData.map((item: any) => item.count ?? item.visitors ?? 0),
      },
    ],
  };

  const botColumns: ColumnsType<any> = [
    {
      title: '店铺',
      dataIndex: 'bot_name',
      key: 'bot_name',
      render: (v: string, r: any) => (
        <div>
          <Text strong>{v || `(已删除 bot${r.bot_id})`}</Text>
          {r.bot_username ? (
            <div><Text type="secondary" style={{ fontSize: 12 }}>@{r.bot_username}</Text></div>
          ) : null}
        </div>
      ),
    },
    {
      title: '今日访客',
      dataIndex: 'today_visitors',
      key: 'today_visitors',
      width: 110,
      sorter: (a: any, b: any) => (a.today_visitors || 0) - (b.today_visitors || 0),
      render: (v: number) => <Text style={{ color: '#fa8c16' }}>{v || 0}</Text>,
    },
    {
      title: '昨日访客',
      dataIndex: 'yesterday_visitors',
      key: 'yesterday_visitors',
      width: 110,
      sorter: (a: any, b: any) => (a.yesterday_visitors || 0) - (b.yesterday_visitors || 0),
      render: (v: number) => <Text style={{ color: '#722ed1' }}>{v || 0}</Text>,
    },
    {
      title: '总访客',
      dataIndex: 'total_visitors',
      key: 'total_visitors',
      width: 110,
      sorter: (a: any, b: any) => (a.total_visitors || 0) - (b.total_visitors || 0),
      render: (v: number) => <Text strong style={{ color: '#1890ff' }}>{v || 0}</Text>,
    },
  ];

  // ===== 搜索结果表格 =====
  const searchUserColumns: ColumnsType<SearchUser> = [
    {
      title: '用户',
      key: 'user',
      render: (_: any, r: SearchUser) => (
        <div>
          <Text strong>{r.username ? `@${r.username}` : '(无用户名)'}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>TG: {r.telegram_id}</Text></div>
        </div>
      ),
    },
    {
      title: '访问天数',
      dataIndex: 'visit_days',
      key: 'visit_days',
      width: 110,
      sorter: (a: SearchUser, b: SearchUser) => a.visit_days - b.visit_days,
      render: (v: number) => <Text strong style={{ color: '#1890ff' }}>{v} 天</Text>,
    },
    {
      title: '逛过店铺',
      dataIndex: 'shop_count',
      key: 'shop_count',
      width: 110,
      sorter: (a: SearchUser, b: SearchUser) => a.shop_count - b.shop_count,
      render: (v: number) => <Tag color="blue">{v} 个</Tag>,
    },
    {
      title: '最近访问',
      dataIndex: 'last_visit',
      key: 'last_visit',
      width: 130,
      render: (v: string) => <Text type="secondary">{v || '-'}</Text>,
    },
  ];

  const searchBotColumns: ColumnsType<SearchBot> = [
    {
      title: '店铺',
      key: 'bot',
      render: (_: any, r: SearchBot) => (
        <div>
          <Text strong>{r.bot_name}</Text>
          {r.bot_username ? (
            <div><Text type="secondary" style={{ fontSize: 12 }}>@{r.bot_username}</Text></div>
          ) : null}
        </div>
      ),
    },
    {
      title: '访问人数',
      dataIndex: 'visitor_count',
      key: 'visitor_count',
      width: 120,
      sorter: (a: SearchBot, b: SearchBot) => a.visitor_count - b.visitor_count,
      render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{v} 人</Text>,
    },
    {
      title: '有访问天数',
      dataIndex: 'visit_days',
      key: 'visit_days',
      width: 120,
      render: (v: number) => <Text>{v} 天</Text>,
    },
    {
      title: '最近访问',
      dataIndex: 'last_visit',
      key: 'last_visit',
      width: 130,
      render: (v: string) => <Text type="secondary">{v || '-'}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">营业中</Tag> : <Tag color="red">已停用</Tag>),
    },
  ];

  const ss = searchResult?.summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">📈 流量监控</Title>
          <Text type="secondary">用户访问商户机器人的统计（每用户每天访问同一店铺只算一次）</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={overviewLoading}>刷新</Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={12} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="今日访客"
              value={stats.today_visitors || 0}
              prefix={<UserOutlined className="text-orange-500" />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="昨日访客"
              value={stats.yesterday_visitors || 0}
              prefix={<UserOutlined className="text-blue-500" />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="7日访客"
              value={stats.seven_day_visitors || 0}
              prefix={<TeamOutlined className="text-green-500" />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="30日访客"
              value={stats.thirty_day_visitors || 0}
              prefix={<RiseOutlined className="text-purple-500" />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ===== 流量搜索 ===== */}
      <Card
        size="small"
        title={<span><SearchOutlined /> 流量搜索</span>}
        extra={<Text type="secondary" style={{ fontSize: 12 }}>支持：用户名 · TG ID · 店铺名 · @机器人用户名</Text>}
      >
        <Space wrap>
          <Input.Search
            placeholder="输入用户名 / TG ID / 店铺名 / @机器人用户名"
            enterButton="搜索"
            allowClear
            style={{ width: 440 }}
            loading={searchLoading}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onSearch={(v) => handleSearch(v)}
          />
          <Select value={searchDays} onChange={setSearchDays} style={{ width: 120 }}>
            <Option value={7}>近 7 天</Option>
            <Option value={30}>近 30 天</Option>
            <Option value={90}>近 90 天</Option>
          </Select>
        </Space>

        {searched && !searchResult && (
          <div style={{ marginTop: 16 }}>
            <Empty description="搜索失败，请稍后重试" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}

        {searchResult && (
          <div style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="匹配用户" value={ss?.matched_users || 0} suffix="人"
                    prefix={<UserOutlined style={{ color: '#1890ff' }} />} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="匹配店铺" value={ss?.matched_bots || 0} suffix="个"
                    prefix={<ShopOutlined style={{ color: '#722ed1' }} />} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Tooltip title="这些店铺在所选时间范围内的去重访客数">
                    <Statistic title="店铺访客合计" value={ss?.total_visitors || 0} suffix="人"
                      valueStyle={{ color: '#52c41a' }} />
                  </Tooltip>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="访问记录" value={ss?.total_records || 0} suffix="条"
                    prefix={<BarChartOutlined style={{ color: '#fa8c16' }} />} />
                </Card>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Text strong>用户明细（{searchResult.users.length}）</Text>
              <Table<SearchUser>
                columns={searchUserColumns}
                dataSource={searchResult.users}
                rowKey="telegram_id"
                size="small"
                style={{ marginTop: 8 }}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                expandable={{
                  rowExpandable: (r) => (r.shops || []).length > 0,
                  expandedRowRender: (r) => (
                    <Table<SearchShop>
                      columns={[
                        { title: '店铺', dataIndex: 'bot_name', key: 'n',
                          render: (v: string, x: SearchShop) => (
                            <span>{v}{x.bot_username ? <Text type="secondary" style={{ fontSize: 12 }}> @{x.bot_username}</Text> : null}</span>
                          ) },
                        { title: '访问天数', dataIndex: 'visit_days', key: 'd', width: 110,
                          render: (v: number) => `${v} 天` },
                        { title: '最近访问', dataIndex: 'last_visit', key: 'l', width: 130,
                          render: (v: string) => <Text type="secondary">{v || '-'}</Text> },
                      ]}
                      dataSource={r.shops}
                      rowKey="bot_id"
                      size="small"
                      pagination={false}
                    />
                  ),
                }}
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <Text strong>店铺明细（{searchResult.bots.length}）</Text>
              <Table<SearchBot>
                columns={searchBotColumns}
                dataSource={searchResult.bots}
                rowKey="bot_id"
                size="small"
                style={{ marginTop: 8 }}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                统计范围：{searchResult.start_date} ~ {searchResult.end_date} ｜ {searchResult.note}
              </Text>
            </div>
          </div>
        )}
      </Card>

      <Card
        title={`近${trendDays}天访客趋势`}
        size="small"
        extra={
          <Select value={trendDays} onChange={setTrendDays} style={{ width: 100 }}>
            <Option value={7}>近7天</Option>
            <Option value={30}>近30天</Option>
            <Option value={90}>近90天</Option>
          </Select>
        }
      >
        <ReactECharts option={chartOption} style={{ height: '350px' }} />
      </Card>

      <Card title="机器人流量排行" size="small"
        extra={<Text type="secondary">总访客：{stats.total_visitors || 0}</Text>}
      >
        <Table
          columns={botColumns}
          dataSource={botList}
          rowKey="bot_id"
          size="small"
          pagination={false}
          loading={botsLoading}
        />
      </Card>
    </div>
  );
};

export default TrafficMonitor;
